<template>
  <BContainer
    v-if="currentUser"
    class="mb-4"
  >

    <div class="d-flex justify-content-end">
      <BBreadcrumb
        v-if="$route.meta.breadcrumb"
        :items="$route.meta.breadcrumb || []"
      />
    </div>

    <BRow
      class="py-2"
    >
      <BCol
        cols="12"
      >
        <BFormGroup
          label="Search"
          label-for="filterSearch"
          label-class="font-weight-bolder"
          class="mb-2"
        >
          <BFormInput
            id="filterSearch"
            v-model="tableRegions.filter.search"
            placeholder="search here"
            autocomplete="off"
            debounce="1000"
            type="text"
          />
        </BFormGroup>
      </BCol>
    </BRow>

    <CustomTable
      ref="tableRegions"
      :provider="tableProvider"
      :fields="tableRegions.fields"
      :filter="tableRegions.filter"
      :filter-on="tableRegions.filterOn"
      :sort-by="tableRegions.sortBy"
      :sort-desc="tableRegions.sortDesc"
      :sort-direction="tableRegions.sortDirection"
      :total-rows="tableRegions.totalRows"
      :current-page="tableRegions.currentPage"
      :per-page="tableRegions.perPage"
      :stacked="tableRegions.stacked"
      :busy="tableRegions.busy"
    >

      <template #cell(action)="{ item }">
        <div class="text-md-nowrap d-flex flex-column flex-md-row justify-content-center">
          <BButton
            size="sm"
            :disabled="tableRegions.busy"
            :variant="Number(item.active) === 1 ? 'danger' : 'primary'"
            @click.prevent="updateRegion(item)"
          >
            {{ Number(item.active) === 1 ? 'Deactivate' : 'Activate' }}
          </BButton>
        </div>
      </template>
    </CustomTable>

    <BModal
      ref="modalRegion"
      scrollable
      no-close-on-esc
      no-enforce-focus
      no-close-on-backdrop
      aria-hidden="false"
      role="dialog"
      :title="MRegionTitle"
    >
      <ValidationObserver
        ref="formRegion"
      >
        <form
          role="form"
          novalidate
          @submit.prevent
        >
          <BRow>
            <BCol
              cols="12"
            >
              <ValidationProvider
                v-slot="{ errors }"
                name="region code"
                vid="region_code"
                rules="required|max:150"
              >
                <BFormGroup
                  label="Region Code"
                  label-for="fRegionCode"
                  label-class="font-weight-bolder"
                  class="mb-2"
                >
                  <BFormInput
                    id="fRegionCode"
                    v-model="formRegion.region_code"
                    type="text"
                    maxlength="150"
                    autocomplete="off"
                    placeholder="enter region code here"
                    :state="errors.length > 0 ? false : null"
                  />
                  <div
                    v-if="(errors.length > 0)"
                    class="invalid-feedback"
                    v-text="errors[0]"
                  />
                </BFormGroup>
              </ValidationProvider>
            </BCol>

            <BCol
              cols="12"
            >
              <ValidationProvider
                v-slot="{ errors }"
                name="region name"
                vid="region_name"
                rules="required|max:150"
              >
                <BFormGroup
                  label="Region Name"
                  label-for="fRegionName"
                  label-class="font-weight-bolder"
                  class="mb-2"
                >
                  <BFormInput
                    id="fRegionName"
                    v-model="formRegion.region_name"
                    type="text"
                    maxlength="150"
                    autocomplete="off"
                    placeholder="enter region name here"
                    :state="errors.length > 0 ? false : null"
                  />
                  <div
                    v-if="(errors.length > 0)"
                    class="invalid-feedback"
                    v-text="errors[0]"
                  />
                </BFormGroup>
              </ValidationProvider>
            </BCol>

            <BCol
              v-if="state.editing"
              cols="12"
            >
              <BFormGroup>
                <BFormCheckbox
                  id="fActive"
                  v-model="formRegion.active"
                  switch
                  value="1"
                  name="active"
                  unchecked-value="0"
                  :disabled="state.busy"
                >
                  Is Active?
                </BFormCheckbox>
              </BFormGroup>
            </BCol>
          </BRow>

        </form>
      </ValidationObserver>

      <template #modal-footer="{cancel}">
        <BButton
          variant="primary"
          :disabled="state.busy"
          @click.prevent="validateRegion"
        >
          {{ state.editing ? 'Update Region' : 'Create Region' }}
        </BButton>
        <BButton
          variant="outline-dark"
          :disabled="state.busy"
          @click.prevent="discardBtnEvent => preventAriaHidden(discardBtnEvent, cancel)"
        >
          Discard
        </BButton>
      </template>

    </BModal>
  </BContainer>
</template>

<script>
import { mapGetters } from 'vuex'

import MxMisc from '@/mixins/misc'
import MxFormatter from '@/mixins/formatter'

import { SAdminRegion } from '@/services/admin'

export default {
  name: 'VAdminRegions',

  mixins: [
    MxMisc,
    MxFormatter
  ],

  middleware: [
    'auth',
    'admin'
  ],

  metaInfo: () => ({
    title: 'Regions'
  }),

  data () {
    return {
      state: {
        busy: false,
        editing: false
      },
      formRegion: {
        id: '',
        region_code: '',
        region_name: '',
        active: 1
      },
      tableRegions: {
        busy: false,
        filter: {
          search: ''
        },
        filterOn: [],
        stacked: 'sm',
        sortBy: 'id',
        sortDesc: false,
        sortDirection: 'asc',
        pageOptions: [10, 25, 50, 100],
        currentPage: 1,
        totalRows: 0,
        perPage: 10,
        fields: [
          { mobile: 0, key: 'index', label: '#', class: 'text-center' },
          { mobile: 5, key: 'action', class: 'text-center w-md-125px' },
          { mobile: 1, key: 'region_code', class: 'min-w-150px' },
          { mobile: 2, key: 'region_name', class: 'min-w-175px' },
          { mobile: 3, key: 'active', class: 'text-md-center w-md-75px', formatter: this.yesOrNo },
          { mobile: 4, key: 'updated_at', class: 'min-w-150px', formatter: this.dateTimeShortFormatter, sortable: true }
        ].sort(this.scaleOnMobile)
      }
    }
  },

  computed: Object.assign({
    MRegionTitle () {
      return this.state.editing ? 'Edit Region' : 'Create Region'
    }
  }, mapGetters({
    currentUser: 'auth/user'
  })),

  methods: {

    async tableProvider ({ currentPage, perPage, sortBy, sortDesc, filter }) {
      this.tableRegions.busy = true
      const payload = {
        page: currentPage,
        per_page: perPage,
        sort: sortBy,
        sort_desc: sortDesc,
        filter_text: filter.search
      }

      return await SAdminRegion.get(payload).then(
        ({ data }) => {
          this.tableRegions.totalRows = data.total_rows
          return data.items
        }
      ).catch(() => {
        this.tableRegions.totalRows = 0
        return []
      }).finally(() => {
        this.tableRegions.busy = false
      })
    },

    createRegion () {
      this.state.editing = false
      this.formRegion = Object.assign(this.formRegion, {
        id: '',
        region_code: '',
        region_name: '',
        active: 1
      })

      this.$refs.modalRegion.show()
    },

    editRegion (region) {
      this.state.editing = true
      this.formRegion = Object.assign(this.formRegion, {
        id: region.id,
        region_code: region.region_code,
        region_name: region.region_name,
        active: region.active
      })

      this.$refs.modalRegion.show()
    },

    async updateRegion (region) {
      const isActive = Number(region.active) === 1

      this.formRegion = Object.assign(this.formRegion, {
        id: region.id,
        region_code: region.region_code,
        region_name: region.region_name,
        active: isActive ? 0 : 1
      })

      this.swalConfirm({
        html: isActive ? '<h6>Deactivate this Region?</h6>' : '<h6>Activate this Region?</h6>',
        confirmButtonText: isActive ? 'Deactivate' : 'Activate',
        customClass: {
          confirmButton: isActive ? 'btn btn-sm btn-danger' : 'btn btn-sm btn-primary'
        },
        preConfirm: async () => {
          this.state.busy = true
          return this.putRegion().finally(() => {
            this.state.busy = false
          })
        }
      })
    },

    async validateRegion () {
      await this.$refs.formRegion.validate().then(
        async validRegion => {
          if (!validRegion) {
            return this.swalInvalid()
          }

          this.swalConfirm({
            html: this.state.editing ? '<h6>Update this Region?</h6>' : '<h6>Create this Region?</h6>',
            confirmButtonText: this.state.editing ? 'Update' : 'Create',
            preConfirm: async () => {
              this.state.busy = true

              const createOrUpdate = async () => {
                return this.state.editing ? this.putRegion() : this.postRegion()
              }

              return createOrUpdate().catch(
                ({ message }) => {
                  this.$refs.formRegion.setErrors(message.errors)
                }
              ).finally(() => {
                this.state.busy = false
              })
            }
          })
        }
      )
    },

    async postRegion () {
      return await SAdminRegion.post(this.formRegion).then(
        ({ data: { message } }) => {
          this.swalSuccess(message).then(() => {
            this.$refs.modalRegion.hide()
            this.$refs.tableRegions.refetch()
          })
        }
      )
    },

    async putRegion () {
      return await SAdminRegion.put(this.formRegion).then(
        ({ data: { message, region } }) => {
          this.swalSuccess(message).then(() => {
            this.$refs.modalRegion.hide()
            this.$refs.tableRegions.updateRow({
              id: region.id,
              region_code: region.region_code,
              region_name: region.region_name,
              active: region.active,
              updated_at: region.updated_at
            })
          })
        }
      )
    }
  }
}
</script>
